import { ApiError, apiErrorFromResponse } from './errors';
import { getToken, emitUnauthorized } from './session';

export type Query = Record<string, string | number | boolean | undefined | null>;

export type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  query?: Query;
  body?: unknown;
  // true면 401이어도 전역 세션만료 처리(redirect) 생략하고 ApiError만 throw.
  suppressAuthRedirect?: boolean;
};

// 같은 도메인의 nginx /api 프록시로 호출(X-API-Key는 nginx가 주입).
const API_BASE = '/api';

function buildUrl(path: string, query?: Query): string {
  const qs = query
    ? Object.entries(query)
        .filter(([, v]) => v !== undefined && v !== null)
        .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
        .join('&')
    : '';
  return API_BASE + path + (qs ? `?${qs}` : '');
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const { method = 'GET', query, body, suppressAuthRedirect } = opts;

  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  let bodyInit: string | undefined;
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    bodyInit = JSON.stringify(body);
  }

  let res: Response;
  try {
    res = await fetch(buildUrl(path, query), { method, headers, body: bodyInit });
  } catch {
    throw new ApiError(0, '서버에 연결할 수 없습니다.');
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? safeJson(text) : undefined;
  if (!res.ok) {
    // 로그인 요청의 401은 제외(LoginPage에서 표면화), 그 외 401은 세션 만료로 전역 처리.
    // suppressAuthRedirect면 호출부가 직접 처리(예: 본인 비번 변경 current 불일치).
    if (res.status === 401 && path !== '/auth/login' && !suppressAuthRedirect) emitUnauthorized();
    throw apiErrorFromResponse(res.status, data);
  }
  return data as T;
}

export const get = <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
  request<T>(path, { ...opts, method: 'GET' });

export const post = <T>(
  path: string,
  body?: unknown,
  opts?: Omit<RequestOptions, 'method' | 'body'>,
) => request<T>(path, { ...opts, method: 'POST', body });

export const patch = <T>(
  path: string,
  body?: unknown,
  opts?: Omit<RequestOptions, 'method' | 'body'>,
) => request<T>(path, { ...opts, method: 'PATCH', body });

export const del = <T>(path: string, opts?: Omit<RequestOptions, 'method' | 'body'>) =>
  request<T>(path, { ...opts, method: 'DELETE' });
